import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { updateHostPort } from '../../apis/Connections';
import { toast } from 'react-toastify';

const SqlHostPortDialog = ({
  open,
  connectionName,
  initialHost,
  initialPort,
  connId,
  onClose,
  onUpdated,
}) => {
  const [host, setHost] = useState(initialHost || '');
  const [port, setPort] = useState(initialPort || '');
  const [hostError, setHostError] = useState('');
  const [portError, setPortError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setHost(initialHost || '');
      setPort(initialPort || '');
      setHostError('');
      setPortError('');
    }
  }, [open, initialHost, initialPort]);

  const handleSave = async () => {
    setHostError('');
    setPortError('');

    const trimmedHost = (host || '').trim();
    const trimmedPort = (port || '').trim();
    let hasError = false;

    if (!trimmedHost) {
      setHostError('Host is required');
      hasError = true;
    }

    if (!trimmedPort) {
      setPortError('Port is required');
      hasError = true;
    } else if (!/^\d+$/.test(trimmedPort)) {
      setPortError('Port must be numeric');
      hasError = true;
    }

    if (hasError) return;
    if (!connId) {
      toast.error('Missing connection identifier for update.');
      return;
    }

    try {
      setSaving(true);
      await updateHostPort(connId, {
        host: trimmedHost,
        port: trimmedPort,
      });
      toast.success('Host & port updated successfully.', { autoClose: 2500 });
      onUpdated?.({ host: trimmedHost, port: trimmedPort });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to update host/port.';
      toast.error(message, { autoClose: 3500 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
        Update SQL Host &amp; Port
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: '#4b5563' }}>
          {connectionName
            ? `Connection: ${connectionName}`
            : 'Update the SQL connection details below.'}
        </Typography>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
          gap={2}
        >
          <TextField
            label="Host"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            error={Boolean(hostError)}
            helperText={hostError}
            disabled={saving}
            fullWidth
          />
          <TextField
            label="Port"
            value={port}
            onChange={(e) => setPort(e.target.value.replace(/[^\d]/g, ''))}
            error={Boolean(portError)}
            helperText={portError}
            disabled={saving}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, pr: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SqlHostPortDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  connectionName: PropTypes.string,
  initialHost: PropTypes.string,
  initialPort: PropTypes.string,
  connId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
};

export default SqlHostPortDialog;

